import React, { useMemo } from "react";
import MultiSelectDropdown from "./MultiSelectDropdown";
import GroupBySelect from "./GroupBySelect";
import DateField from "./DateField";
import { isoDateToBR, shiftMonthsISO, shiftDaysISO } from "./dateUtils";
import { useTranslation } from "../../hooks/useTranslation";

/* Card de filtros, fiel ao mockup:
   - segmented Entre datas / Mês / Dia com setas ◀▶ para navegar o período
   - multi-selects Serviços/Produtos, Empresas (super), Unidades, Células,
     Usuários (cascateados em memória pelos vínculos do filter_options)
   - busca por nome do processamento, checkbox de integração, Limpar/Aplicar */
const CalIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const PALETTE = [
  "#D84E2B", "#643184", "#1976d2", "#16a34a", "#f59e0b", "#0ea5e9",
  "#db2777", "#0d9488", "#7c3aed", "#b45309", "#0284c7", "#65a30d",
];

const ConsumptionFilters = ({
  draft,
  setDraft,
  options, // resposta do /filter_options
  role,
  onApply,
  onClear,
  loading = false,
  cycleHint = "",
}) => {
  const { t } = useTranslation("consumption");
  const isSuper = role === "super_admin";

  const setField = (field, value) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  /* ------- opções dos combos (cascata em memória) ------- */
  const productOptions = useMemo(
    () =>
      (options?.products || []).map((p, i) => ({
        key: p.code,
        label: `${p.service_label} → ${p.label}`,
        color: PALETTE[i % PALETTE.length],
      })),
    [options]
  );

  const companyOptions = useMemo(
    () =>
      (options?.companies || []).map((c) => ({
        key: String(c.id),
        label: c.name,
      })),
    [options]
  );

  const selectedCompanyIds = draft.companyIds; // null = todas
  const tenantOptions = useMemo(() => {
    let tenants = options?.tenants || [];
    if (isSuper && selectedCompanyIds) {
      const allowed = new Set(selectedCompanyIds.map(Number));
      tenants = tenants.filter((tn) => allowed.has(tn.company_id));
    }
    return tenants.map((tn) => ({ key: String(tn.id), label: tn.name }));
  }, [options, isSuper, selectedCompanyIds]);

  const allowedTenantIds = useMemo(() => {
    const visible = new Set(tenantOptions.map((o) => Number(o.key)));
    if (!draft.tenantIds) return visible;
    return new Set(draft.tenantIds.map(Number).filter((id) => visible.has(id)));
  }, [tenantOptions, draft.tenantIds]);

  const tenantNameById = useMemo(() => {
    const map = {};
    (options?.tenants || []).forEach((tn) => {
      map[tn.id] = tn.name;
    });
    return map;
  }, [options]);

  const cellOptions = useMemo(
    () =>
      (options?.cells || [])
        .filter((c) => allowedTenantIds.has(c.tenant_id))
        .map((c) => ({
          key: String(c.id),
          label: `${tenantNameById[c.tenant_id] || ""}→${c.name}`,
        })),
    [options, allowedTenantIds, tenantNameById]
  );

  const userOptions = useMemo(
    () =>
      (options?.users || [])
        .filter((u) => allowedTenantIds.has(u.tenant_id))
        .map((u) => ({
          key: String(u.id),
          label: `${tenantNameById[u.tenant_id] || ""}→${u.name}`,
          inactive: u.inactive,
        })),
    [options, allowedTenantIds, tenantNameById]
  );

  /* ------- período (datas via DateField; setas abaixo) ------- */
  const shiftRange = (deltaMonths) => {
    setDraft((prev) => {
      const start = shiftMonthsISO(prev.dateStart, deltaMonths);
      const end = shiftMonthsISO(prev.dateEnd, deltaMonths);
      return {
        ...prev,
        dateStart: start,
        dateEnd: end,
        dateStartText: isoDateToBR(start),
        dateEndText: isoDateToBR(end),
      };
    });
  };

  const shiftMonth = (delta) => {
    setDraft((prev) => {
      let m = prev.refMonth + delta;
      let y = prev.refYear;
      if (m < 0) { m = 11; y -= 1; }
      if (m > 11) { m = 0; y += 1; }
      return { ...prev, refMonth: m, refYear: y };
    });
  };

  const shiftDay = (delta) => {
    setDraft((prev) => {
      const day = shiftDaysISO(prev.refDay, delta);
      return { ...prev, refDay: day, refDayText: isoDateToBR(day) };
    });
  };

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        key: String(i),
        label: t(`months.${i}`),
      })),
    [t]
  );
  const currentYear = new Date().getFullYear();
  const yearOptions = useMemo(
    () =>
      Array.from({ length: 4 }, (_, i) => {
        const y = currentYear - 3 + i + 1;
        return { key: String(y), label: String(y) };
      }),
    [currentYear]
  );

  const activeFiltersCount = [
    draft.productCodes,
    draft.companyIds,
    draft.tenantIds,
    draft.cellIds,
    draft.userIds,
  ].filter((v) => v !== null).length +
    (draft.jobName ? 1 : 0) +
    (draft.includeIntegration ? 0 : 1);

  return (
    <section className="card card-pad filters-card">
      <div className="filters-top">
        <div className="filters-title">
          <span className="ico">
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 3H2l8 9.46V19l4 2v-8.54z" />
            </svg>
          </span>
          {t("filters")}
          {activeFiltersCount > 0 && (
            <span className="count">{activeFiltersCount}</span>
          )}
        </div>
        <div className="seg">
          <button
            type="button"
            className={draft.periodMode === "range" ? "on" : ""}
            onClick={() => setField("periodMode", "range")}
          >
            <CalIcon />
            {t("betweenDates")}
          </button>
          <button
            type="button"
            className={draft.periodMode === "month" ? "on" : ""}
            onClick={() => setField("periodMode", "month")}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M3 10h18" />
            </svg>
            {t("month")}
          </button>
          <button
            type="button"
            className={draft.periodMode === "day" ? "on" : ""}
            onClick={() => setField("periodMode", "day")}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" />
            </svg>
            {t("day")}
          </button>
        </div>
      </div>

      <div className="filters-body">
        <div className="period-row">
          <div className="period-cluster">
            {draft.periodMode === "range" && (
              <>
                <button type="button" className="nudge" title={t("previousPeriod")} onClick={() => shiftRange(-1)}>
                  <ChevronLeftIcon />
                </button>
                <DateField
                  label={t("startDate")}
                  value={draft.dateStart}
                  text={draft.dateStartText}
                  onChange={(iso, masked) =>
                    setDraft((prev) => ({
                      ...prev,
                      dateStartText: masked,
                      ...(iso ? { dateStart: iso } : {}),
                    }))
                  }
                />
                <span className="range-arrow">→</span>
                <DateField
                  label={t("endDate")}
                  value={draft.dateEnd}
                  text={draft.dateEndText}
                  onChange={(iso, masked) =>
                    setDraft((prev) => ({
                      ...prev,
                      dateEndText: masked,
                      ...(iso ? { dateEnd: iso } : {}),
                    }))
                  }
                />
                <button type="button" className="nudge" title={t("nextPeriod")} onClick={() => shiftRange(1)}>
                  <ChevronRightIcon />
                </button>
              </>
            )}
            {draft.periodMode === "month" && (
              <>
                <button type="button" className="nudge" onClick={() => shiftMonth(-1)}>
                  <ChevronLeftIcon />
                </button>
                <div className="field" style={{ minWidth: 150 }}>
                  <label>{t("month")}</label>
                  <GroupBySelect
                    options={monthOptions}
                    value={String(draft.refMonth)}
                    onChange={(v) => setField("refMonth", Number(v))}
                  />
                </div>
                <div className="field" style={{ minWidth: 110 }}>
                  <label>{t("year")}</label>
                  <GroupBySelect
                    options={yearOptions}
                    value={String(draft.refYear)}
                    onChange={(v) => setField("refYear", Number(v))}
                  />
                </div>
                <button type="button" className="nudge" onClick={() => shiftMonth(1)}>
                  <ChevronRightIcon />
                </button>
              </>
            )}
            {draft.periodMode === "day" && (
              <>
                <button type="button" className="nudge" onClick={() => shiftDay(-1)}>
                  <ChevronLeftIcon />
                </button>
                <DateField
                  label={t("day")}
                  value={draft.refDay}
                  text={draft.refDayText}
                  onChange={(iso, masked) =>
                    setDraft((prev) => ({
                      ...prev,
                      refDayText: masked,
                      ...(iso ? { refDay: iso } : {}),
                    }))
                  }
                />
                <button type="button" className="nudge" onClick={() => shiftDay(1)}>
                  <ChevronRightIcon />
                </button>
              </>
            )}
          </div>
          <div className="hint">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="9" /><path d="M12 8v4l3 2" />
            </svg>
            {draft.periodMode === "range" && cycleHint}
            {draft.periodMode === "month" && t("monthHint")}
            {draft.periodMode === "day" && t("dayHint")}
          </div>
        </div>

        <div className="filters-grid">
          <div className="field wide">
            <label>{t("servicesProducts")}</label>
            <MultiSelectDropdown
              options={productOptions}
              value={draft.productCodes}
              onChange={(v) => setField("productCodes", v)}
              allLabel={t("allProducts")}
              withSwatch
            />
          </div>
          {isSuper && (
            <div className="field">
              <label>{t("companies")}</label>
              <MultiSelectDropdown
                options={companyOptions}
                value={draft.companyIds}
                onChange={(v) =>
                  setDraft((prev) => ({
                    ...prev,
                    companyIds: v,
                    // re-escopa seleções dependentes ao mudar empresas
                    tenantIds: null,
                    cellIds: null,
                    userIds: null,
                  }))
                }
                allLabel={t("allCompanies")}
                searchable
              />
            </div>
          )}
          <div className="field">
            <label>{t("units")}</label>
            <MultiSelectDropdown
              options={tenantOptions}
              value={draft.tenantIds}
              onChange={(v) =>
                setDraft((prev) => ({
                  ...prev,
                  tenantIds: v,
                  cellIds: null,
                  userIds: null,
                }))
              }
              allLabel={t("allUnits")}
              searchable
            />
          </div>
          <div className="field">
            <label>{t("cells")}</label>
            <MultiSelectDropdown
              options={cellOptions}
              value={draft.cellIds}
              onChange={(v) => setField("cellIds", v)}
              allLabel={t("allCells")}
              searchable
            />
          </div>
          <div className="field">
            <label>{t("users")}</label>
            <MultiSelectDropdown
              options={userOptions}
              value={draft.userIds}
              onChange={(v) => setField("userIds", v)}
              allLabel={t("allUsers")}
              searchable
            />
          </div>
          <div className={`field ${isSuper ? "" : "wide"}`}>
            <label>{t("jobNameSearch")}</label>
            <div className="input">
              <span className="ico">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
                </svg>
              </span>
              <input
                value={draft.jobName}
                onChange={(e) => setField("jobName", e.target.value)}
                placeholder={t("jobNamePlaceholder")}
              />
            </div>
          </div>
        </div>

        <div className="row-actions">
          <label
            className={`check ${draft.includeIntegration ? "on" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              setField("includeIntegration", !draft.includeIntegration);
            }}
          >
            <span className="box">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </span>
            {t("includeIntegration")}
          </label>
          <div className="actions-right">
            <button type="button" className="btn btn-ghost" onClick={onClear}>
              {t("clear")}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onApply}
              disabled={loading}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 3H2l8 9.46V19l4 2v-8.54z" />
              </svg>
              {loading ? t("loading") : t("applyFilters")}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export { PALETTE };
export default ConsumptionFilters;
